import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  User,
  ArrowLeft,
  LogOut,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Bell,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [targetCountry, setTargetCountry] = useState(user?.target_country || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applicationCountry, setApplicationCountry] = useState("");
  const [applicationBio, setApplicationBio] = useState("");
  const [saving, setSaving] = useState(false);

  const handleUpdateProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_country: targetCountry, bio }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        toast.success("Profile updated!");
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Error updating profile");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyLocalHomey = async () => {
    if (!applicationCountry || !applicationBio) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API}/users/apply-local-homey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          country: applicationCountry,
          bio: applicationBio,
        }),
      });

      if (response.ok) {
        toast.success("Application submitted! An admin will review it.");
        setDialogOpen(false);
        // Refresh user data
        const userResponse = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (userResponse.ok) {
          setUser(await userResponse.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit application");
      }
    } catch (error) {
      toast.error("Error submitting application");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("lh_session_token");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("lh_session_token");
      navigate("/", { replace: true });
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const getStatusBadge = () => {
    if (user?.role === "local_homey") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#48BB78]/20 text-[#48BB78] text-sm">
          <CheckCircle className="w-4 h-4" />
          Approved Local Homey
        </span>
      );
    }
    if (user?.local_homey_status === "pending") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#ED8936]/20 text-[#ED8936] text-sm">
          <Clock className="w-4 h-4" />
          Application Pending
        </span>
      );
    }
    if (user?.local_homey_status === "rejected") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#E53E3E]/20 text-[#E53E3E] text-sm">
          <XCircle className="w-4 h-4" />
          Application Rejected
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Profile</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#4A6C6F] text-white text-2xl">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="font-heading text-2xl font-semibold text-[#1A202C]">{user?.name}</h2>
                <p className="text-[#718096]">{user?.email}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#4A6C6F]/10 text-[#4A6C6F] text-sm capitalize">
                    {user?.role === "local_homey" ? (
                      <>
                        <Users className="w-4 h-4" />
                        Local Homey
                      </>
                    ) : user?.role === "admin" ? (
                      <>
                        <Shield className="w-4 h-4" />
                        Admin
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4" />
                        Newbie
                      </>
                    )}
                  </span>
                  {getStatusBadge()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl mb-6">
          <CardHeader>
            <CardTitle className="text-[#1A202C]">Profile Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="text-sm font-medium text-[#1A202C] mb-2 block">
                Target Country
              </label>
              <Select value={targetCountry} onValueChange={setTargetCountry}>
                <SelectTrigger data-testid="target-country-select">
                  <SelectValue placeholder="Select your destination" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
                  <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
                  <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#1A202C] mb-2 block">Bio</label>
              <Textarea
                data-testid="bio-input"
                placeholder="Tell us about yourself..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <Button
              data-testid="save-profile-btn"
              onClick={handleUpdateProfile}
              disabled={saving}
              className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </CardContent>
        </Card>

        {/* Notification Settings Link */}
        <Card 
          data-testid="notification-settings-link"
          className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl mb-6 cursor-pointer hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
          onClick={() => navigate("/notifications")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#4A6C6F]/10 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-[#4A6C6F]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#1A202C]">Notification Settings</h4>
                  <p className="text-sm text-[#4A5568]">
                    Manage email and push notification preferences
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#718096]" />
            </div>
          </CardContent>
        </Card>

        {/* Local Homey Application */}
        {user?.role === "newbie" && !user?.local_homey_status && (
          <Card className="bg-[#F5F5F0] border-0 rounded-2xl mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D97757]/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#D97757]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-[#1A202C]">Become a Local Homey</h4>
                  <p className="text-sm text-[#4A5568]">
                    Already living in your destination country? Help other newcomers!
                  </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="apply-btn"
                      className="bg-[#D97757] hover:bg-[#C96747] text-white rounded-full"
                    >
                      Apply Now
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Apply to be a Local Homey</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm font-medium text-[#1A202C] mb-2 block">
                          Which country do you live in?
                        </label>
                        <Select value={applicationCountry} onValueChange={setApplicationCountry}>
                          <SelectTrigger data-testid="application-country-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
                            <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
                            <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#1A202C] mb-2 block">
                          Tell us about your experience
                        </label>
                        <Textarea
                          data-testid="application-bio-input"
                          placeholder="How long have you been living there? What areas can you help with?"
                          value={applicationBio}
                          onChange={(e) => setApplicationBio(e.target.value)}
                          className="min-h-[120px]"
                        />
                      </div>
                      <Button
                        data-testid="submit-application-btn"
                        onClick={handleApplyLocalHomey}
                        className="w-full bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                      >
                        Submit Application
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logout */}
        <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
          <CardContent className="p-6">
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={handleLogout}
              className="w-full border-[#E53E3E] text-[#E53E3E] hover:bg-[#E53E3E]/10 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
